import { useEffect, useState, type ReactElement } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './lib/supabase'
import { pullAll, flushOutbox } from './lib/sync'
import LoginPage from './pages/LoginPage'
import RecordPage from './pages/RecordPage'
import DetailsPage from './pages/DetailsPage'
import OverviewPage from './pages/OverviewPage'
import MorePage from './pages/MorePage'
import ReminderPage from './pages/ReminderPage'
import SyncBar from './components/SyncBar'
import './App.css'

type Page = 'record' | 'overview' | 'reminder' | 'details' | 'more'

const NAV_ICONS: Record<Page, ReactElement> = {
  record: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
    </svg>
  ),
  overview: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  reminder: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  details: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
      <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
  ),
  more: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/>
    </svg>
  ),
}

const NAV_ITEMS: { page: Page; label: string }[] = [
  { page: 'record',   label: '記一筆' },
  { page: 'overview', label: '總覽'   },
  { page: 'reminder', label: '提醒'   },
  { page: 'details',  label: '明細'   },
  { page: 'more',     label: '更多'   },
]

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined)
  const [page, setPage] = useState<Page>('record')

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session))
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s)
      if (s) {
        pullAll().catch(console.warn)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // 連線恢復時 flush outbox
  useEffect(() => {
    const handler = () => flushOutbox().catch(console.warn)
    window.addEventListener('online', handler)
    return () => window.removeEventListener('online', handler)
  }, [])

  if (session === undefined) return null   // 等 auth 初始化
  if (!session) return <LoginPage />

  return (
    <div className="app">
      <SyncBar />
      <main className="app-main">
        <div className="page-content">
          {page === 'record'   && <RecordPage />}
          {page === 'overview' && <OverviewPage />}
          {page === 'reminder' && <ReminderPage />}
          {page === 'details'  && <DetailsPage />}
          {page === 'more'     && <MorePage />}
        </div>
      </main>

      <nav className="bottom-nav">
        {NAV_ITEMS.map(({ page: p, label }) => (
          <button
            key={p}
            className={`nav-item${page === p ? ' active' : ''}`}
            onClick={() => setPage(p)}
          >
            <span className="nav-pill">
              <span className="nav-icon">{NAV_ICONS[p]}</span>
            </span>
            <span className="nav-label">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  )
}
